import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import { DeleteOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import type { FieldArrayWithId } from "react-hook-form"

import {
  INVENTORY_LOT_COLUMN_COUNT,
  INVENTORY_PALLET_COLUMN_COUNT,
  InventoryLotTableHeaderCells,
  InventoryLotTableRowCells,
  InventoryPalletTableHeaderCells,
  InventoryPalletTableRowCells,
} from "../../../../components"
import type { TransferPalletSelectionFormValues } from "../../schemas"

import {
  TRANSFER_PALLET_DESTINATION_COLUMN_COUNT,
  TransferPalletDestinationFields,
} from "./TransferPalletDestinationFields"

import type { InventoryPalletGroup, WarehouseArea } from "@/graphql/codegen/graphql"
import { formatNumber } from "@/utils/number"
import { isNil } from "@/utils/utils"

const QTY_TO_MOVE_COLUMN_COUNT = 1
const DELETE_COLUMN_COUNT = 1

const COLUMN_COUNT =
  INVENTORY_PALLET_COLUMN_COUNT +
  INVENTORY_LOT_COLUMN_COUNT +
  QTY_TO_MOVE_COLUMN_COUNT +
  TRANSFER_PALLET_DESTINATION_COLUMN_COUNT +
  DELETE_COLUMN_COUNT

interface TransferPalletDestinationsProps {
  warehouseArea: WarehouseArea
  destinationFields: FieldArrayWithId<TransferPalletSelectionFormValues, "destinations">[]
  selectedPallets: InventoryPalletGroup[]
  selectedShelvingIds: string[]
  uomName: string
  onPalletRemove: (palletIndex: number) => void
}

export const TransferPalletDestinations: FC<TransferPalletDestinationsProps> = ({
  warehouseArea,
  destinationFields,
  selectedPallets,
  selectedShelvingIds,
  uomName,
  onPalletRemove,
}) => {
  return (
    <div className='flex min-h-0 flex-1 flex-col rounded-lg border border-gray-400 p-4'>
      <Typography variant='h6' className='mb-4 font-bold leading-10 text-gray-800'>
        2. Specify Locations
      </Typography>
      <TableContainer className='overflow-auto'>
        <Table stickyHeader>
          <TableHead className='bg-gray-100'>
            <TableRow>
              <InventoryPalletTableHeaderCells />
              <InventoryLotTableHeaderCells quantityColumnLabel='Current Lot UoM' />
              <TableCell className='min-w-[90px] whitespace-nowrap'>Qty to Move</TableCell>
              <TableCell className='min-w-[160px] whitespace-nowrap'>Area*</TableCell>
              <TableCell className='min-w-[140px] whitespace-nowrap'>Aisle</TableCell>
              <TableCell className='min-w-[120px] whitespace-nowrap'>Shelf</TableCell>
              <TableCell className='min-w-[100px] whitespace-nowrap'>Bin</TableCell>
              <TableCell className='w-12' />
            </TableRow>
          </TableHead>
          <TableBody>
            {!destinationFields.length && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className='py-8'>
                  <p className='flex flex-col items-center gap-y-2 text-center'>
                    <span className='font-semibold text-gray-700'>No pallets selected</span>
                    <span className='max-w-[340px]'>
                      Select pallet(s) from the table above using the checkboxes to specify locations.
                    </span>
                  </p>
                </TableCell>
              </TableRow>
            )}
            {destinationFields.map((field, palletIndex) => {
              const pallet = selectedPallets[palletIndex]
              if (!pallet) return null

              return pallet.lots.map((lot, lotIndex) => {
                const isFirstLot = lotIndex === 0

                return (
                  <TableRow
                    key={`${field.id}-${lot.rawInventoryId}`}
                    data-testid={`transfer.pallet-destinations.row-${lot.rawInventoryId}`}
                  >
                    <InventoryPalletTableRowCells pallet={pallet} isFirstLot={isFirstLot} uomName={uomName} />
                    <InventoryLotTableRowCells lot={lot} uomName={uomName} showStatusIcon={false} />
                    <TableCell className='whitespace-nowrap'>
                      {isNil(lot.totalUnits) ? "" : `${formatNumber(lot.totalUnits)} ${uomName}`}
                    </TableCell>
                    {isFirstLot ? (
                      <>
                        <TransferPalletDestinationFields
                          index={palletIndex}
                          warehouseArea={warehouseArea}
                          selectedShelvingIds={selectedShelvingIds}
                        />
                        <TableCell>
                          <Tooltip title='Remove pallet'>
                            <IconButton
                              size='small'
                              onClick={() => onPalletRemove(palletIndex)}
                              data-testid={`transfer.pallet-destinations.delete-button-${palletIndex}`}
                            >
                              <DeleteOutlined />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </>
                    ) : (
                      <TableCell
                        colSpan={TRANSFER_PALLET_DESTINATION_COLUMN_COUNT + DELETE_COLUMN_COUNT}
                        className='h-[60px]'
                      />
                    )}
                  </TableRow>
                )
              })
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}
